import { ChangeDetectionStrategy, Component, computed, inject, ViewEncapsulation } from "@angular/core";
import { LayoutWidget } from "../layouts/se.layout.widget.abstract";
import { Layouts } from "../layouts/se.layouts";
import { SEEventDetailsStore } from "./se.event.details.store";
import { S25ContactProfileImageComponent } from "../contact/contact.profile.image.component";
import { S25Util } from "../../../util/s25-util";
import { ContactService } from "../../../services/contact.service";
import { S25IconComponent } from "../../s25-icon/s25.icon.component";

@Component({
    standalone: true,
    selector: "s25-ng-se-event-details-contact-roles-widget",
    template: `
        @for (contact of contacts(); track contact.id) {
            <div class="contact">
                <s25-ng-contact-profile-image [contactId]="contact.id" [name]="contact.name" />

                <div class="contact-details">
                    <div class="contact-header">
                        <h4 class="contact-name">{{ contact.name }}</h4>
                        <p class="contact-roles">
                            @for (role of contact.roles; track role) {
                                <div class="contact-role">{{ role }}</div>
                            }
                        </p>
                    </div>
                    @if (!!contact.email) {
                        <p class="contact-email">
                            <s25-ng-icon [type]="'mailFilled'" [size]="18" [ariaLabel]="false" />
                            <a [href]="'mailto:' + contact.email">{{ contact.email }}</a>
                        </p>
                    }
                </div>
            </div>
        }
    `,
    styles: `
        :host {
            display: grid;
            gap: 1rem;
        }

        .contact {
            background: var(--color--surface-bg);
            border-radius: 16px;
            color: var(--color--text);
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid var(--color--border);
            transition: all 0.3s ease;
            overflow: hidden;
            position: relative;
            display: flex;
            gap: 1.25rem;
            padding: 0.5rem;
            align-items: center;
        }

        .contact-details {
            display: grid;
            row-gap: 0.25rem;
            align-self: start;
            flex-grow: 1;
        }

        .contact-header {
            display: flex;
            gap: 0.5rem;
        }

        .contact-name {
            margin: 0;
            font-size: 1.3rem;
            font-weight: 600;
            color: var(--color--text-strong);
            line-height: 1.2;
        }

        .contact-roles {
            display: flex;
            gap: 0.5rem;
            font-size: 0.8em;
            align-items: center;

            .contact-role {
                background-color: var(--cn-blue);
                padding: 0.25em;
                border-radius: 3px;
                color: white;
                align-content: center;
                line-height: 0;
                height: 1.5em;
            }
        }

        .contact-email {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            color: var(--color--text-muted);
        }

        .contact-email a {
            color: var(--color--link-text);
            text-decoration: none;
            font-weight: 500;
            transition: color 0.2s ease;
        }

        .contact-email a:hover {
            color: var(--color--link-text-hover);
            text-decoration: underline;
        }

        :host ::ng-deep .contact-email svg {
            opacity: 0.7;
            color: var(--color--text-muted);
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25ContactProfileImageComponent, S25IconComponent],
})
export class SeEventDetailsContactRolesWidgetComponent extends LayoutWidget<
    typeof SeEventDetailsContactRolesWidgetComponent
> {
    static readonly META = {
        name: "Event Contacts",
        settings: [],
        defaultSettings: {},
    } as const satisfies Layouts.WidgetStatic;

    protected readonly eventStore = inject(SEEventDetailsStore);

    readonly contacts = computed(() => {
        const roles = this.eventStore.contactRoles() ?? [];
        // Group by contact
        const byContact = S25Util.array.groupBy(roles, (role) => role.contact.contactId!);
        return S25Util.entries(byContact).map(([contactId, roles]) => {
            const contact = roles![0].contact;
            return {
                id: contactId,
                name: ContactService.getFullName(contact.firstName, contact.familyName),
                email: contact.email,
                roles: roles!.map((role) => role.name),
            };
        });
    });
}
